import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useTreatments } from "@/hooks/useTreatments";
import { useReferral } from "@/hooks/useReferral";
import { useBillingPortal } from "@/hooks/useBillingPortal";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { OrderTracker } from "@/components/OrderTracker";
import { TruckIcon } from "@/components/icons";
import type { TreatmentRow, TreatmentStatus } from "@/lib/database.types";

const badgeFor: Record<TreatmentStatus, { tone: "active" | "pending" | "muted" | "warn"; label: string }> = {
  pending_review: { tone: "pending", label: "Pending review" },
  active:         { tone: "active",  label: "Active" },
  paused:         { tone: "muted",   label: "Paused" },
  discontinued:   { tone: "warn",    label: "Discontinued" },
};

export default function MyTreatment() {
  const { session } = useSession();
  const location = useLocation();
  const patientQ = usePatient(session?.user.id);
  const treatmentsQ = useTreatments(patientQ.data?.id);
  const referralQ = useReferral(patientQ.data?.id);

  const loading = patientQ.isLoading || treatmentsQ.isLoading;

  // Support deep links like /treatment#orders (Quick Actions "Track order").
  useEffect(() => {
    if (loading || !location.hash) return;
    document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, location.hash]);

  if (loading) return <LoadingScreen />;

  const treatments = treatmentsQ.data ?? [];
  const referralAction = referralQ.data?.next_action_url ?? null;

  return (
    <AppLayout>
      <PageHeader title="My treatment" subtitle="Your active plan, dosing, and shipments." />

      {treatments.length === 0 ? (
        <EmptyState
          title="No treatment yet"
          body="Your plan will appear here once your provider has reviewed your intake and approved a regimen."
          cta={
            referralAction ? (
              <a
                href={referralAction}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-fh-green hover:bg-fh-green-dark px-4 py-2.5 text-[13px] font-semibold text-white"
              >
                Open my care portal →
              </a>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {treatments.map((t) => (
              <TreatmentCard key={t.id} treatment={t} />
            ))}
          </div>

          <OrdersSection treatments={treatments} />
        </>
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Orders & delivery — every shipment across the patient's treatments, each
// with the Ordered → Shipped → In transit → Delivered stages infographic.
// Backed by our shipments rows today; swaps to live Bask order tracking when
// headless API access lands (see lib/orderTracking.ts).
function OrdersSection({ treatments }: { treatments: TreatmentRow[] }) {
  const orders = treatments.flatMap((t) =>
    (t.shipments ?? []).map((s) => ({ shipment: s, treatment: t })),
  );
  orders.sort((a, b) => b.shipment.created_at.localeCompare(a.shipment.created_at));

  return (
    <section id="orders" className="mt-8 scroll-mt-6">
      <div className="flex items-center justify-between mb-3.5 gap-3">
        <h2 className="font-serif text-[20px] font-normal text-fh-ink m-0">
          Orders &amp; delivery
        </h2>
        <Link
          to="/messages"
          className="text-[12.5px] font-semibold text-fh-green hover:text-fh-green-dark"
        >
          Manage next shipment →
        </Link>
      </div>

      {orders.length === 0 ? (
        <Card>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-[10px] bg-fh-sage flex items-center justify-center shrink-0 text-fh-green-dark">
              <TruckIcon />
            </div>
            <p className="text-[14px] text-fh-gray leading-relaxed m-0">
              No orders yet. When your medication ships, you'll see each order here with
              live stages — ordered, in transit, and delivered.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {orders.map(({ shipment, treatment }) => (
            <Card key={shipment.id}>
              <OrderTracker shipment={shipment} medication={treatment.medication} />
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function TreatmentCard({ treatment }: { treatment: TreatmentRow }) {
  const badge = badgeFor[treatment.status];
  const latestShipment = treatment.shipments?.[0];

  return (
    <Card
      title={treatment.medication}
      action={treatment.provider?.full_name ?? undefined}
    >
      <div className="flex items-baseline gap-2 mb-3">
        {treatment.dose && (
          <span className="text-[14px] text-fh-gray">{treatment.dose}</span>
        )}
        {treatment.started_at && (
          <span className="text-[13px] text-fh-gray">
            · Started {new Date(treatment.started_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <StatusBadge label={badge.label} tone={badge.tone} />

      {latestShipment && (
        <div className="mt-4 pt-4 border-t border-fh-border text-[13px] text-fh-gray leading-relaxed">
          <span className="font-semibold text-fh-ink">Latest shipment:</span>{" "}
          {latestShipment.status}
          {latestShipment.shipped_at && (
            <> · shipped {new Date(latestShipment.shipped_at).toLocaleDateString()}</>
          )}
          {latestShipment.tracking_number && latestShipment.carrier && (
            <div className="mt-1">
              {latestShipment.carrier} · {latestShipment.tracking_number}
            </div>
          )}
        </div>
      )}

      {treatment.monthly_price !== null && (
        <div className="mt-3 text-[13px] text-fh-gray">
          Plan price:{" "}
          <span className="font-semibold text-fh-ink">
            ${Number(treatment.monthly_price).toFixed(2)}/mo
          </span>
        </div>
      )}

      {/* Subscription status: next billing date + cancel-at-period-end notice.
          These come from stripe-webhook updates on the treatments row. */}
      <SubscriptionMeta treatment={treatment} />

      {/* Single "Manage subscription" CTA. Opens Stripe's hosted Customer
          Portal where the patient can cancel, update payment, or download
          invoices — Stripe handles all the FTC Click-to-Cancel / ROSCA /
          CCPA / ARL surface so we don't have to re-implement it ourselves. */}
      {treatment.stripe_subscription_id && (
        <ManageSubscriptionRow />
      )}
    </Card>
  );
}

function SubscriptionMeta({ treatment }: { treatment: TreatmentRow }) {
  const hasSub = !!treatment.stripe_subscription_id;
  if (!hasSub) return null;

  const periodEndDate = treatment.current_period_end
    ? new Date(treatment.current_period_end)
    : null;
  const periodEndLabel = periodEndDate
    ? periodEndDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  if (treatment.cancel_at_period_end) {
    return (
      <div className="mt-3 text-[13px] text-amber-800">
        <span className="font-semibold">Cancellation scheduled.</span>{" "}
        {periodEndLabel
          ? <>You'll keep access through {periodEndLabel}, then your plan ends.</>
          : <>You'll keep access through the end of your current billing cycle.</>}
      </div>
    );
  }

  if (treatment.status === "active" && periodEndLabel) {
    return (
      <div className="mt-3 text-[13px] text-fh-gray">
        Next charge: <span className="font-semibold text-fh-ink">{periodEndLabel}</span>
      </div>
    );
  }

  return null;
}

function ManageSubscriptionRow() {
  const portal = useBillingPortal();

  return (
    <div className="mt-4 pt-4 border-t border-fh-border">
      {portal.error && (
        <div className="mb-3">
          <Alert tone="error">
            {portal.error.message ||
              "Couldn't open billing portal. Please email care@fortehealth.co."}
          </Alert>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12.5px] text-fh-gray m-0 leading-relaxed">
          Update payment method, download invoices, or cancel anytime.
        </p>
        <Button
          variant="secondary"
          loading={portal.isPending}
          onClick={() => portal.mutate()}
        >
          Manage subscription
        </Button>
      </div>
    </div>
  );
}
